/**
 * t627u batch C: visitors sign themselves in and out on their own phone, with no login.
 * A building's reception QR code (10 characters) and a visitor pass for someone who's expected (16 characters) both open
 * /visit/<code>. The page shows only the building's name and its site rules; host search (names only) is off unless the
 * building turns it on. Signing out needs the token this phone was given at sign-in. Passes are codes only: nothing is
 * emailed; staff pass the link or QR on themselves.
 */
import { DateTime } from 'luxon';

export const RECEPTION_CODE_RE = /^[a-hjkmnp-z2-9]{10}$/;
export const VISIT_PASS_CODE_RE = /^[a-hjkmnp-z2-9]{16}$/;
export const isVisitCode = (s: string | null | undefined) => Boolean(s && (RECEPTION_CODE_RE.test(s) || VISIT_PASS_CODE_RE.test(s)));

export const visitUrl = (origin: string, code: string) => `${origin.replace(/\/+$/, '')}/visit/${code}`;
/** `/visit/<code>` → the code, else null. */
export function visitCodeFromPath(path: string): string | null {
  const m = /^\/visit\/([a-z0-9]+)\/?$/.exec(path);
  return m && isVisitCode(m[1]) ? m[1] : null;
}

/** What the public page may know. No list of who's on site, no staff, nothing about the workspace beyond the building's name. */
export interface ReceptionInfo {
  kind: 'reception' | 'pass';
  building: string;
  rules: string | null;
  host_search: boolean;
  /** A pass: the visitor's first name and their host's first name, and whether it can still be used today. */
  pass?: { first_name: string; host_first_name: string | null; state: 'ready' | 'not_today' | 'used' | 'cancelled' } | null;
}
/** A host search hit: a name and an opaque key (not the person's id). */
export interface ReceptionHost { key: string; name: string }
export interface ReceptionSignIn { name?: string; company?: string | null; host_key?: string | null; car_reg?: string | null; rules_accepted?: boolean; client_event_id: string }
export interface ReceptionResult { message: string; host_told: boolean; out_token?: string }

export const RECEPTION_HOST_MIN = 3;
export const RECEPTION_HOST_MAX = 6;

/** Where the phone keeps its sign-out token for a code (one visit at a time per code). */
export const receptionTokenKey = (code: string) => `hub.visit.${code}`;

// ------------------------------------------------------------------------------------------------ passes (staff side)
export interface VisitorPass {
  id: string;
  location_id: string;
  location_name: string;
  code: string;
  name: string;
  company: string | null;
  host_user_id: string | null;
  host_name: string | null;
  expected_on: string;
  created_by_name: string | null;
  created_at: string;
  cancelled_at: string | null;
  used_at: string | null;
}
export interface VisitorPassInput { location_id: string; name: string; company?: string | null; host_user_id?: string | null; expected_on: string }

export function visitorPassProblem(b: { name?: string | null; company?: string | null; expected_on?: string | null }, today?: string): string | null {
  const name = (b.name ?? '').trim();
  if (name.length < 2) return 'Enter the visitor’s full name';
  if (name.length > 80) return 'Name is too long';
  if ((b.company ?? '').trim().length > 80) return 'Company is too long';
  if (!b.expected_on || !DateTime.fromISO(b.expected_on).isValid) return 'Choose the day they’re coming';
  if (today && b.expected_on < today) return 'Choose today or a day after';
  return null;
}

export function visitorPassPayload(b: VisitorPassInput): VisitorPassInput {
  const t = (s?: string | null) => (s ?? '').trim() || null;
  return { location_id: b.location_id, name: (b.name ?? '').trim().replace(/\s+/g, ' '), company: t(b.company), host_user_id: b.host_user_id || null, expected_on: b.expected_on };
}

/** "Thu 5 Nov · Acme Lifts · seeing Phoebe Mensah", then "Signed in" or "Cancelled" when it's been used or stopped. */
export function visitorPassLine(p: Pick<VisitorPass, 'expected_on' | 'company' | 'host_name' | 'used_at' | 'cancelled_at'>): string {
  const d = DateTime.fromISO(p.expected_on).setLocale('en-GB').toFormat('ccc d LLL');
  const state = p.cancelled_at ? 'Cancelled' : p.used_at ? 'Signed in' : null;
  return [d, p.company, p.host_name ? `seeing ${p.host_name}` : null, state].filter(Boolean).join(' · ');
}

export const VISITOR_PASS_NOTE = 'A visitor pass is a link and QR code for one visit on the day. Send it to them yourself; The Hub doesn’t email it. They open it on their phone to sign in and out.';
export const RECEPTION_NOTE = 'Visitors scan it with their own phone to sign in and out. It needs no login and shows nothing but the building’s name and site rules.';
